import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductDetailState } from "./productDetail.state";
import { ProductDetailData } from "./productDetail.types";
import {
  getProductDetail,
  getBottomGroupProducts,
  getModalContent,
} from "./productDetail.thunk";
import { toggleStoreFollow } from "../store/store.thunk";

const initialState: ProductDetailState = {
  data: null,
  status: "idle",
  error: null,
  bottomGroupProducts: {},
  bottomGroupStatus: {},
  modalContent: null,
  modalType: null,
  modalStatus: "idle",
  selectedColorIndex: 0,
  selectedSizeIndex: -1,
  loadingColorIndex: null,
  isColorChanging: false,
  pendingColorSlug: null,
  currentSlug: null,
  isAuthDataSettled: false,
};

export const productDetailSlice = createSlice({
  name: "productDetail",
  initialState,
  reducers: {
    resetProductDetail: () => initialState,
    resetModalContent: (state) => {
      state.modalContent = null;
      state.modalType = null;
      state.modalStatus = "idle";
    },
    setSelectedColorIndex: (state, action: PayloadAction<number>) => {
      state.selectedColorIndex = action.payload;
    },
    setSelectedSizeIndex: (state, action: PayloadAction<number>) => {
      state.selectedSizeIndex = action.payload;
    },
    setSelectedIndices: (
      state,
      action: PayloadAction<{ colorIndex: number; sizeIndex: number }>,
    ) => {
      state.selectedColorIndex = action.payload.colorIndex;
      state.selectedSizeIndex = action.payload.sizeIndex;
    },
    setLoadingColorIndex: (state, action: PayloadAction<number | null>) => {
      state.loadingColorIndex = action.payload;
    },
    setIsColorChanging: (state, action: PayloadAction<boolean>) => {
      state.isColorChanging = action.payload;
    },
    setPendingColorSlug: (state, action: PayloadAction<string | null>) => {
      state.pendingColorSlug = action.payload;
    },
    setCurrentSlug: (state, action: PayloadAction<string>) => {
      state.currentSlug = action.payload;
    },
    // Favori durumu renk varyantı üzerinde tutuluyor (API is_favorited'ı
    // variants_by_color altında döndürüyor). Toggle sonrası tek kaynak burası
    // güncellenir; ürün detayındaki üç kalp de (ProductActions, sticky header,
    // mobil bar) aynı yerden okuduğu için hepsi birlikte değişir.
    setVariantFavorite: (
      state,
      action: PayloadAction<{ variantId: number; isFavorited: boolean }>,
    ) => {
      const { variantId, isFavorited } = action.payload;
      const product = state.data?.product;
      const variant = product?.variants_by_color?.find(
        (v) => v.variant_id === variantId,
      );
      if (variant) variant.is_favorited = isFavorited;
      if (product && product.variant_id === variantId) {
        product.is_favorited = isFavorited;
      }
    },
    // SSR'dan gelen veriyi Redux'a yükler — bottomGroup/selectedIndex'i resetlemez,
    // action.meta.arg olmadığı için manuel dispatch'te isSilent undefined olur sorununu çözer
    preloadFromSSR: (state, action: PayloadAction<{ data: ProductDetailData; slug: string }>) => {
      const { data } = action.payload;
      state.status = "success";
      state.data = data;
      state.error = null;
      // SSR/ISR verisi anonim — auth'a bağlı alanlar henüz kesin değil.
      state.isAuthDataSettled = false;
      state.isColorChanging = false;
      state.loadingColorIndex = null;
      state.pendingColorSlug = null;
      state.bottomGroupProducts = {};
      state.bottomGroupStatus = {};
      const product = data?.product;
      const variants = product?.variants_by_color;
      const slugIdx = variants?.findIndex((v) => v.slug === product?.slug) ?? -1;
      state.selectedColorIndex = slugIdx !== -1 ? slugIdx : 0;
      state.selectedSizeIndex = -1;
      if (product?.slug) {
        state.currentSlug = product.slug;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductDetail.pending, (state, action) => {
        // silent=true: auth re-fetch veya arka plan güncellemesi — mevcut içeriği koru
        const isSilent = action.meta?.arg?.silent;
        const willSetLoading = !state.isColorChanging && !isSilent;
        if (willSetLoading) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(getProductDetail.fulfilled, (state, action) => {
        const isSilent = action.meta?.arg?.silent;
        state.status = "success";
        state.data = action.payload.data;
        state.error = null;
        state.isColorChanging = false;
        state.loadingColorIndex = null;
        state.pendingColorSlug = null;
        // Client fetch token taşır — auth'a bağlı alanlar artık kesin.
        state.isAuthDataSettled = true;
        // silent fetch (auth re-fetch) sırasında bottomGroup ve index'leri resetleme —
        // bu değerler zaten doğru, sıfırlamak tüm alt section'ların yeniden yüklenmesine neden olur
        if (!isSilent) {
          state.bottomGroupProducts = {};
          state.bottomGroupStatus = {};
          const product = action.payload.data?.product;
          const variants = product?.variants_by_color;
          const slugIdx = variants?.findIndex((v) => v.slug === product?.slug) ?? -1;
          state.selectedColorIndex = slugIdx !== -1 ? slugIdx : 0;
          state.selectedSizeIndex = -1;
        }
        const product = action.payload.data?.product;
        if (product?.slug) {
          state.currentSlug = product.slug;
        }
      })
      .addCase(getProductDetail.rejected, (state, action) => {
        const isSilent = action.meta?.arg?.silent;
        // Silent fetch (auth re-fetch) reject olursa mevcut veriyi ve status'u koru —
        // data=null + status="error" yaparsak sayfa not-found'a düşer
        if (isSilent) {
          state.isColorChanging = false;
          state.loadingColorIndex = null;
          state.pendingColorSlug = null;
          // Auth re-fetch başarısız olsa da beklemeyi bitir — aksi hâlde
          // auth'a bağlı alanların placeholder'ı kalıcı olarak asılı kalır.
          state.isAuthDataSettled = true;
          return;
        }
        state.status = "error";
        state.data = null;
        state.error =
          action.payload ?? "Ürün detayı yüklenirken bir hata oluştu.";
        state.loadingColorIndex = null;
        state.isColorChanging = false;
        state.pendingColorSlug = null;
      })
      .addCase(getBottomGroupProducts.pending, (state, action) => {
        const key = action.meta.arg.key;
        state.bottomGroupStatus[key] = "loading";
      })
      .addCase(getBottomGroupProducts.fulfilled, (state, action) => {
        const key = action.meta.arg.key;
        state.bottomGroupProducts[key] = action.payload.data;
        state.bottomGroupStatus[key] = "success";
      })
      .addCase(getBottomGroupProducts.rejected, (state, action) => {
        const key = action.meta.arg.key;
        state.bottomGroupStatus[key] = "error";
      })
      .addCase(getModalContent.pending, (state, action) => {
        state.modalStatus = "loading";
        state.modalType = action.meta.arg.type;
      })
      .addCase(getModalContent.fulfilled, (state, action) => {
        state.modalStatus = "success";
        state.modalContent = action.payload.data;
      })
      .addCase(getModalContent.rejected, (state) => {
        state.modalStatus = "error";
      })
      .addCase(toggleStoreFollow.fulfilled, (state, action) => {
        if (
          state.data?.product?.seller &&
          state.data.product.seller.id === action.meta.arg
        ) {
          state.data.product.seller.is_following = action.payload.is_following;
          state.data.product.seller.follower_count =
            action.payload.follower_count;
        }
      });
  },
});

export const {
  resetProductDetail,
  resetModalContent,
  setSelectedColorIndex,
  setSelectedSizeIndex,
  setSelectedIndices,
  setLoadingColorIndex,
  setIsColorChanging,
  setPendingColorSlug,
  setCurrentSlug,
  setVariantFavorite,
  preloadFromSSR,
} = productDetailSlice.actions;

export default productDetailSlice.reducer;
